import {
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { StripeCardNumberElement } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { MODE_DELIVERY, MODE_TAKEAWAY } from "../../../constants/constants";
import { languageData } from "../../../i18n";
import ErrorIcon from "../../../images/ErrorIcon.svg";
import { Restaurant, RestaurantCart } from "../../../interfaces/Catalogue";
import {
  CodePromoSuggestion,
  LoyalityGroup,
  LoyaltyClient,
  PanierDiscount,
  PromoType,
} from "../../../interfaces/loyality.interface";
import {
  mapToCreateDeliveryRequest,
  mapToOrderPayload,
  mapToOrderSummary,
} from "../../../mapper/orderPayloadMapper";
import { orderApi, paymentApi } from "../../../services/apiGate";
import {
  clearStoreItem,
  createPaymentAction,
} from "../../../store/actions/actions";
import { selectedTimeRightISO } from "../../../utils/dateUtils";
import { getTotalOrder } from "../../../utils/priceUtils";
import OnlinePaymentSection from "../OnlinePaymentSection/OnlinePaymentSection";
import "./CheckoutForm.scss";
import RestaurantLoader from "../../common/RestaurantLoader/RestaurantLoader.";

interface CheckoutFormProps {
  restaurant: Restaurant;
  restaurantCart: RestaurantCart | undefined;

  isOnlinePaymentChecked: boolean;
  isMaintenantChecked: boolean;

  isValidTime: boolean;
  selectedTimeRight: string;
  quoteId?: string;
  estimatedDeliveryQuoteMinutes: number;

  supTotal: number;
  fees: number;
  panierDiscount?: PanierDiscount | null;
  codePromoSuggestion?: CodePromoSuggestion;
  groupDiscount?: LoyalityGroup;

  discountAmount: number;
  finalTotal: number;

  totalLoyaltyPtsConsomed: number;
  userLoyalityPoints: number;
  promoCode: string;
  appliedPromo: PromoType;

  setLoyalityClient: (rewardPoints: LoyaltyClient) => void;
  setqrcode: (str: string) => void;
  setcommand: (command: any) => void;
  setpromptCommand: (value: boolean) => void;
  setShowProcessingCommand: () => void;
  setOnlinePaymentChecked: (value: boolean) => void;

  [key: string]: any;
}

function CheckoutForm(props: CheckoutFormProps) {
  const {
    restaurant,
    restaurantCart,

    isOnlinePaymentChecked,
    isMaintenantChecked,
    appliedPromo,

    panierDiscount,
    codePromoSuggestion,
    groupDiscount,

    quoteId,
    estimatedDeliveryQuoteMinutes,
    supTotal,
    fees,
    finalTotal,
    discountAmount,
    userLoyalityPoints,
    totalLoyaltyPtsConsomed,
    promoCode,
    setLoyalityClient,
    setOnlinePaymentChecked,
  } = props;
  const dispatch = useDispatch();
  const [paymentMethod, setPaymentMethod] = useState<"wallet" | "card">("card");

  const { userInfo, payementSuccess, payRetries, payementResponse } =
    useSelector((state: any) => ({
      userInfo: state.auth.userInfo,
      payementSuccess: state.payment.payementSuccess,
      payRetries: state.payment.payRetries,
      payementResponse: state.payment.payementResponse,
    }));

  const isPanierDiscountApplied = appliedPromo == "PANIER";
  const isPromoCodeApplied = appliedPromo == "CODE_PROMO";
  const isPromoGroupApplied = appliedPromo == "GROUP";

  const stripe = useStripe();

  const deliveryAddress: google.maps.places.PlaceResult | undefined =
    useSelector((state: any) => state.delivery.deliveryAddress);
  const [error, setError] = useState("");
  const [isPaymentBtnDisabled, setIsPaymentBtnDisabled] = useState(false);
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // For Apple Pay / Google Pay
  const [appleGooglePayRequest, setAppleGooglePayRequest] = useState<
    import("@stripe/stripe-js").PaymentRequest | null
  >(null);
  const [isAppleGooglePayAvailable, setIsAppleGooglePayAvailable] =
    useState(false);
  const [isCheckingPaymentSupport, setIsCheckingPaymentSupport] =
    useState(true);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [paymentErrorMessage, setPaymentErrorMessage] = useState<string | null>(
    null
  );

  const [debugLogs, setDebugLogs] = useState<string[]>([]);

  const [showDebug, setShowDebug] = useState(false);

  const logStep = (message: string) => {
    // console.log("[DEBUG]", message); // still log to console
    // setDebugLogs((prev) => [
    //   ...prev,
    //   `${new Date().toISOString()}: ${message}`,
    // ]);
  };

  const handleSubmitClick = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await handleSubmit(new Event("submit"));
    } finally {
      // setTimeout(() => {
      //   setIsSubmitting(false);
      // }
      // , 1000);
    }
  };

  useEffect(() => {
    // console.log("🚀 ~ useEffect ~ payementSuccess:", payementSuccess)
    try {
      if (payementSuccess === true) {
        window.scrollTo(0, 0);
        document.querySelector(".paimentSection")?.classList.add("slideUpHide");
        document
          .querySelector(".InvoiceCommandComponent")
          ?.classList.add("DisplayInvoice");
        props.setShowProcessingCommand();
        setLoyalityClient(payementResponse?.loyalty?.LoyalityClient);
        props.setpromptCommand(true);
        props.setqrcode(payementResponse?.QrCode);

        setTimeout(() => {
          setTimeout(() => {
            document
              .querySelector(".InvoiceCommandComponent")
              ?.classList.add("DisplayInvoice");
            fetchOrderDetails("" + payementResponse?.commandId);
          }, 1000);
        }, 6000);

        dispatch(clearStoreItem("payementSuccess"));
        dispatch(clearStoreItem("payementResponse"));
      } else if (payementSuccess === false) {
        if (payementResponse?.status == "requires_action")
          confirmpay(
            payementResponse?.clientSecret,
            payementResponse?.commandId
          );
        else {
          // console.log("payementResponse", payementResponse);
          if (payementResponse?.error) {
            setIsSubmitting(false);
            setError(
              "Impossible de finaliser la commande. Veuillez réessayer ultérieurement."
            );
            /* navigate(
              "PaymentError",
              { order: { ...order }, error: payementResponse?.error },
              false
            ); */
          }
        }

        //
      }
    } catch (error) {}
    return () => {
      dispatch(clearStoreItem("payementSuccess"));
    };
  }, [payementSuccess, payRetries]);

  useEffect(() => {
    setIsPaymentBtnDisabled(totalLoyaltyPtsConsomed > userLoyalityPoints);
    setError(
      totalLoyaltyPtsConsomed > userLoyalityPoints
        ? "Vous n'avez pas assez de points de fidélité pour effectuer ce paiement."
        : ""
    );
  }, [userLoyalityPoints, totalLoyaltyPtsConsomed]);

  useEffect(() => {
    // clear panier on payment success
    if (payementSuccess) {
      dispatch(clearStoreItem("restaurantCart"));
    }
  }, [payementSuccess]);

  useEffect(() => {
    if (codePromoSuggestion) {
      setIsPaymentBtnDisabled(
        supTotal < (codePromoSuggestion?.cartThreshold || 0)
      );
    } else {
      setIsPaymentBtnDisabled(false);
    }
  }, [codePromoSuggestion, supTotal]);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    setIsCheckingPaymentSupport(true);

    const paymentRequest = stripe.paymentRequest({
      country: "FR",
      currency: "eur",
      total: {
        label: "Total",
        amount: Math.round(finalTotal * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    paymentRequest.canMakePayment().then((result) => {
      if (result) {
        setIsAppleGooglePayAvailable(true);
        setAppleGooglePayRequest(paymentRequest);
      } else {
        setIsAppleGooglePayAvailable(false);
      }
      setIsCheckingPaymentSupport(false);
    });

    paymentRequest.on("paymentmethod", async (event) => {
      setIsPaymentProcessing(true);
      setPaymentErrorMessage(null);

      try {
        await ___handleCreatePayment(event.paymentMethod.id);
        event.complete("success");
      } catch (err) {
        event.complete("fail");
        setPaymentErrorMessage("Payment failed. Please try another method.");
      } finally {
        setIsPaymentProcessing(false);
      }
    });
  }, [finalTotal]);

  const ___handleCreatePayment = async (payId: any, payLater?: boolean) => {
    // console.log(' ___handleCreatePayment restaurant:', restaurant)

    logStep(
      `Step 1: ___handleCreatePayment STARTED (payId: ${payId || "N/A"}, payLater: ${!!payLater})`
    );
    // console.log("[DEBUG] ___handleCreatePayment called with:", { payId, payLater });

    if (!restaurantCart) {
      logStep("Step 2: restaurantCart is MISSING - aborting");
      console.warn("[DEBUG] restaurantCart not found. Payment cannot proceed.");
      return;
    }

    try {
      logStep("Step 3: Preparing delivery request and order payload...");

      const PrepareFor = props.isMaintenantChecked
        ? selectedTimeRightISO(props.selectedTimeRight)
        : null;

      logStep(
        `Step 4: PrepareFor calculated: ${PrepareFor || "Not scheduled"}`
      );

      let createDeliveryRequest;

      if (quoteId && restaurantCart.mode === MODE_DELIVERY) {
        createDeliveryRequest = mapToCreateDeliveryRequest(
          deliveryAddress,
          quoteId,
          userInfo,
          estimatedDeliveryQuoteMinutes
        );
      }

      logStep("Step 5: Delivery request mapped successfully");

      const completeAddress = `${createDeliveryRequest?.clientAddress?.streetAddress || ""}, ${createDeliveryRequest?.clientAddress?.city || ""}, ${createDeliveryRequest?.clientAddress?.state || ""}, ${createDeliveryRequest?.clientAddress?.zip_code || ""}, ${createDeliveryRequest?.clientAddress?.country || ""}`;
      logStep(`Step 6: Complete Address ready: ${completeAddress}`);

      // Step 7 - Guard and detailed debug logs
      logStep("Step 7: Calculating total order and order summary...");
      // console.log("[DEBUG] restaurantCart.categories:", restaurantCart.categories);
      // console.log("[DEBUG] restaurantCart.mode:", restaurantCart.mode);

      if (
        !restaurantCart?.categories ||
        !Array.isArray(restaurantCart.categories)
      ) {
        setIsSubmitting(false);
        throw new Error(
          "Invalid restaurantCart.categories (missing or not an array)"
        );
      }

      const totalOrder = getTotalOrder(restaurantCart.categories);
      logStep(`Step 7.1: Total Order calculated: ${totalOrder}`);

      const orderSummary = mapToOrderSummary(
        restaurantCart.categories,
        restaurantCart.mode
      );
      logStep("Step 7.2: Order summary mapped successfully");

      const orderPayload = mapToOrderPayload(
        restaurantCart,
        restaurant,
        totalOrder,
        isOnlinePaymentChecked,
        isMaintenantChecked,
        isPanierDiscountApplied,
        isPromoCodeApplied,
        isPromoGroupApplied,

        PrepareFor,
        userInfo,
        finalTotal,
        userLoyalityPoints,
        createDeliveryRequest,
        completeAddress,
        discountAmount,

        codePromoSuggestion,
        panierDiscount,
        groupDiscount,
        fees
      );

      logStep("Step 8: Order payload mapped");

      const orderDetailed = restaurantCart.categories;

      const createPaymentBody = {
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.email,
        phone: userInfo.phoneNumber,
        restaurantId: restaurant.id,
        orderSummary,
        orderDetailed: JSON.stringify(orderDetailed),
        command: JSON.stringify(orderPayload),
        remise: "null",
        payLater: !!payLater,
        address: completeAddress || "",
        idempotencyKey: "" + uuidv4(),
        loyaltySecretKey: "",
        codePromo: promoCode,
        paymentMethodId: "" + payId,
        diningMode:
          restaurantCart.mode === MODE_TAKEAWAY
            ? "takeaway"
            : restaurantCart.mode === MODE_DELIVERY
              ? "delivery"
              : "onspot",
        PrepareFor,
        qrCode: "",
        isDelivery: restaurantCart.mode === MODE_DELIVERY,
        ...(restaurantCart.mode === MODE_DELIVERY && { createDeliveryRequest }),
        discount:
          isPanierDiscountApplied || isPromoCodeApplied || isPromoGroupApplied
            ? discountAmount
            : 0,
        loyaltyPointsConsumed: isPanierDiscountApplied
          ? panierDiscount?.cartRewardTotalPtRequired
          : 0,
        groupId: isPromoGroupApplied ? groupDiscount?.id : undefined,
        deliveryFee: fees,
      };

      logStep("Step 9: Final payment body prepared");
      // console.log("[DEBUG] Final createPaymentBody:", createPaymentBody);

      // Dispatch payment creation
      logStep("Step 10: Dispatching createPayment action...");
      dispatch(createPaymentAction(createPaymentBody));
      logStep("Step 11: createPayment dispatched successfully");
    } catch (error: any) {
      logStep("Step X: ERROR during payment creation");
      console.error("[DEBUG] Error in ___handleCreatePayment:", {
        message: error?.message,
        stack: error?.stack,
        error,
      });
      logStep(`Error message: ${error?.message || "Unknown error"}`);
      logStep(`Error stack: ${error?.stack || "No stack trace available"}`);
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event: any) => {
    logStep("handleSubmit triggered");
    setError("");

    if (fees == 0 && restaurantCart?.mode == MODE_DELIVERY) {
      setError("Problème de livraison. Veuillez réessayer plus tard.");
      return;
    }

    if (props.isOnlinePaymentChecked) {
      const cardElementt: StripeCardNumberElement | null | undefined =
        elements?.getElement(CardNumberElement);
      event.preventDefault();

      if (!stripe || !elements) {
        return;
      }

      if (cardElementt) {
        window.scrollTo(0, 0);
        stripe
          .createPaymentMethod({
            type: "card",
            card: cardElementt,
          })
          .then(function (result) {
            if (!result.error) {
              ___handleCreatePayment(result.paymentMethod?.id);
            }
            if (result.error) {
              setIsSubmitting(false);
              setError("Votre numéro de carte est incomplet.");
            }
          })
          .catch((error) => {
            setError("Erreur de paiement.");
            setIsSubmitting(false);
          });
      }
    } else {
      ___handleCreatePayment(null, true);
    }
  };

  const confirmpay = async (sc: string, commandId: number) => {
    try {
      const resposne = await stripe?.confirmCardPayment(sc);

      let paymentIntent = resposne?.paymentIntent;

      if (paymentIntent) {
        // console.log(paymentIntent?.status);
        let response = await paymentApi.confirmPayment({
          commandId: commandId,
          paymentIntentId: paymentIntent?.id,
          idempotencyKey: uuidv4(),
          codePromo: null,
          status: paymentIntent?.status ?? "canceled",
        });
        window.scrollTo(0, 0);
        document.querySelector(".paimentSection")?.classList.add("slideUpHide");
        props.setShowProcessingCommand();
        setLoyalityClient(response.data?.loyalty?.LoyalityClient || {});
        setTimeout(() => {
          window.location.href = "#/mes-commandes";
        }, 6000);
      } else {
        setIsSubmitting(false);
        setError("Echec de paiement, veuillez réessayer.");
      }
    } catch (error) {
      console.error("🚀 ~ confirmpay ~ error:", error);
      setIsSubmitting(false);
      setError("Echec de paiement");
    }
  };

  const fetchOrderDetails = async (id: string) => {
    try {
      let response = await orderApi.getOrderById(id);
      let orderDetails = response.data;
      if (orderDetails) {
        props.setcommand(orderDetails?.commande);
      }
    } catch (error) {
      console.error("🚀 ~ fetchOrderDetails ~ error:", error);
    }
  };

  const handlePaymentMethodChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOnlinePaymentChecked(event.target.value === "online");
  };

  return (
    <div className="cardContainer">
      {/* Toggle Debug Modal */}

      {showDebug && (
        <div
          className="debug-modal-overlay"
          onClick={() => setShowDebug(false)}
        >
          <div
            className="debug-modal"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
          >
            <h3>Debug Logs</h3>
            <div className="debug-content">
              {debugLogs.length ? (
                debugLogs.map((log, idx) => <h4 key={idx}>{log}</h4>)
              ) : (
                <p>No logs yet.</p>
              )}
            </div>
            <button onClick={() => setShowDebug(false)} className="close-btn">
              Close
            </button>
          </div>
        </div>
      )}

      <div className={isSubmitting ? "OverylayLoading" : ""}></div>

      <div>
        <div
          className="fl_col gp20"
          style={{
            margin: "0 auto",
            padding: "10px",
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            borderRadius: "12px",
          }}
        >
          {restaurantCart?.mode != MODE_DELIVERY && (
            <div>
              <p className="block-title">Méthode de paiement</p>
              <div className="payments-wrapper">
                {restaurantCart?.restaurant?.isOnSpotPaymentActive && (
                  <div className="onsite-payment">
                    <input
                      type="radio"
                      id="onsite"
                      name="payment-methode"
                      value="onsite"
                      checked={!isOnlinePaymentChecked}
                      onChange={handlePaymentMethodChange}
                      disabled={restaurantCart?.mode == MODE_DELIVERY}
                    />
                    <label htmlFor="onsite">Sur place</label>
                  </div>
                )}
                {restaurantCart?.restaurant?.isStripeActive && (
                  <div className="online-payment">
                    <input
                      type="radio"
                      id="online"
                      name="payment-methode"
                      value="online"
                      checked={isOnlinePaymentChecked}
                      onChange={handlePaymentMethodChange}
                    />
                    <label htmlFor="online">En ligne</label>
                  </div>
                )}
              </div>
            </div>
          )}
          {isCheckingPaymentSupport ? (
            <RestaurantLoader
              variant="dots"
              message="Vérification des options de paiement..."
              size="small"
            />
          ) : (
            <OnlinePaymentSection
              isOnlinePaymentChecked={props.isOnlinePaymentChecked}
              isCheckingPaymentSupport={isCheckingPaymentSupport}
              paymentErrorMessage={paymentErrorMessage}
              isAppleGooglePayAvailable={isAppleGooglePayAvailable}
              appleGooglePayRequest={appleGooglePayRequest}
              isPaymentProcessing={isPaymentProcessing}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
            />
          )}
        </div>
      </div>

      {error && (
        <p className="msg_error">
          <img src={ErrorIcon} alt="" />
          {error}
        </p>
      )}

      {((paymentMethod == "card" && isOnlinePaymentChecked) ||
        !isOnlinePaymentChecked) &&
        !isCheckingPaymentSupport && (
          <div className="submit-order">
            {isSubmitting ? (
              <div className="BtnLoader">
                <div className="disabledClass"></div>
                <button onClick={handleSubmit} disabled>
                  <p>Chargement</p>
                  <div style={{ transform: "translateX(30px)" }}>
                    <div className="loaderBtn"></div>
                  </div>
                </button>
              </div>
            ) : (
              <span>
                <button
                  disabled={
                    !props.isValidTime || isPaymentBtnDisabled || isSubmitting
                  }
                  onClick={handleSubmitClick}
                  className={`${!props.isValidTime || isPaymentBtnDisabled || isSubmitting ? "btn-disabled" : ""}`}
                >
                  <p>
                    {isOnlinePaymentChecked
                      ? languageData.fr.payment.btnText
                      : "Valider ma commande"}
                  </p>
                </button>
              </span>
            )}

            {/* <button
          className="debug-btn"
          onClick={() => setShowDebug(true)}

        >
          Show Debug Logs
        </button> */}
          </div>
        )}
    </div>
  );
}

export default CheckoutForm;
